import { useEffect, useState } from 'react'
import Stack from '@mui/material/Stack'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import theme from '../../../helpers/theme'
import ExpandMoreSharpIcon from '@mui/icons-material/ExpandMoreSharp'
import {
  customReportCalcTypes,
  customReportEndDateAdjustments,
  customReportStartDateAdjustments,
} from '../../../layout/dashboard/reports/constants'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

const textFieldStyle = {
  size: 'small',
  variant: 'outlined',
  InputProps: {
    sx: {
      borderRadius: 0,
    },
  },
  sx: {
    backgroundColor: 'white',
    disableUnderline: true,
  },
  fullWidth: true,
}

const selectStyle = {
  size: 'small',
  IconComponent: ExpandMoreSharpIcon,
  sx: {
    borderRadius: 0,
    '&.Mui-disabled': {
      backgroundColor: theme.palette.secondary[100],
    },
  },
  fullWidth: true,
}

const buttonStyle = {
  variant: 'outlined',
  disableElevation: true,
  size: 'small',
  sx: { fontSize: 14, textTransform: 'none' },
}

const CustomReportColumn = ({ columnData, removeColumn, saveColumn, shiftRight, shitfLeft }) => {
  const [colData, setColData] = useState(columnData)
  const [submitReady, setSubmitReady] = useState(false)
  const [editing, setEditing] = useState(true)

  useEffect(() => {
    if (!colData.header_name || !colData.column_type || !colData.end_date_adjustment) {
      setSubmitReady(false)
      return
    }
    if (['NT', 'IF', 'OF', 'BC'].includes(colData.column_type)) {
      if (!colData.start_date_adjustment) {
        setSubmitReady(false)
        return
      }
      if (['LXD', 'LXW', 'LXM'].includes(colData.start_date_adjustment)) {
        if (!colData.start_date_adjustment_units) {
          setSubmitReady(false)
          return
        }
      }
    }
    setSubmitReady(true)
  }, [colData])

  return (
    <Stack
      sx={{
        minWidth: '200px',
        width: '200px',
        height: '500px',
        border: 1,
        borderColor: theme.palette.secondary.light,
      }}
      padding={1}
      justifyContent='space-between'
      spacing={1}
    >
      <Stack>
        <Typography>Column Name</Typography>
        <TextField
          {...textFieldStyle}
          disabled={!editing}
          value={colData.header_name}
          onChange={(event) => {
            setColData((prevState) => {
              return {
                ...prevState,
                header_name: event.target.value,
              }
            })
          }}
        />
        <Typography>Calculation Type</Typography>
        <Select
          {...selectStyle}
          disabled={!editing}
          value={colData.column_type}
          onChange={(event) => {
            setColData((prevState) => {
              return {
                ...prevState,
                column_type: event.target.value,
              }
            })
          }}
        >
          {customReportCalcTypes.map((item) => {
            return (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            )
          })}
        </Select>
        {colData?.column_type && (
          <>
            <Stack spacing={0.5} direction='row' alignItems='center'>
              <Typography>{['CB', 'OB'].includes(colData.column_type) ? 'Date' : 'End Date'}</Typography>
              <Tooltip
                arrow
                placement='right'
                title={
                  <Typography>{`${
                    ['CB', 'OB'].includes(colData.column_type) ? 'Date' : 'End Date'
                  } is relative to the run date`}</Typography>
                }
              >
                <InfoOutlined color='secondary' />
              </Tooltip>
            </Stack>
            <Select
              {...selectStyle}
              value={colData.end_date_adjustment}
              disabled={!editing}
              onChange={(event) => {
                setColData((prevState) => {
                  return {
                    ...prevState,
                    end_date_adjustment: event.target.value,
                  }
                })
              }}
            >
              {customReportEndDateAdjustments.map((item) => {
                return (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                )
              })}
            </Select>
            {!['CB', 'OB'].includes(colData.column_type) && (
              <>
                <Stack spacing={0.5} direction='row' alignItems='center'>
                  <Typography>Start Date</Typography>
                  <Tooltip arrow placement='right' title={<Typography>Start date is relative to End Date</Typography>}>
                    <InfoOutlined color='secondary' />
                  </Tooltip>
                </Stack>
                <Select
                  {...selectStyle}
                  value={colData.start_date_adjustment}
                  disabled={!editing}
                  onChange={(event) => {
                    setColData((prevState) => {
                      return {
                        ...prevState,
                        start_date_adjustment: event.target.value,
                      }
                    })
                  }}
                >
                  {customReportStartDateAdjustments.map((item) => {
                    return (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    )
                  })}
                </Select>
              </>
            )}
          </>
        )}

        {!['CB', 'OB'].includes(colData.column_type) &&
          ['LXD', 'LXW', 'LXM'].includes(colData.start_date_adjustment) && (
            <>
              <Typography># Days/Weeks/Months</Typography>
              <TextField
                {...textFieldStyle}
                value={colData.start_date_adjustment_units}
                type='number'
                disabled={!editing}
                onChange={(event) => {
                  setColData((prevState) => {
                    return {
                      ...prevState,
                      start_date_adjustment_units: event.target.value,
                    }
                  })
                }}
              />
            </>
          )}
      </Stack>
      <Stack spacing={1}>
        {editing ? (
          <>
            <Button
              color='primary'
              disabled={!submitReady}
              {...buttonStyle}
              onClick={() => {
                setEditing(false)
                saveColumn(colData)
              }}
            >
              Submit
            </Button>
            <Button color='error' {...buttonStyle} onClick={removeColumn}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Stack direction='row' spacing={1} justifyContent='space-between'>
              <Button
                disableRipple
                color='primary'
                {...buttonStyle}
                onClick={shitfLeft}
                sx={{ width: '50%', fontSize: '14px', textTransform: 'none' }}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                disableRipple
                color='primary'
                {...buttonStyle}
                onClick={shiftRight}
                sx={{ width: '50%', fontSize: '14px', textTransform: 'none' }}
              >
                <ArrowForwardIcon />
              </Button>
            </Stack>
            <Button
              color='primary'
              {...buttonStyle}
              onClick={() => {
                setEditing(true)
              }}
            >
              Edit
            </Button>
            <Button color='error' {...buttonStyle} onClick={removeColumn}>
              Remove
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  )
}

export default CustomReportColumn
